/**
 * As preparações em curso e as falhas em aberto, para a lista de casos da equipe (E200.F2, GUS-220,
 * ADR-0143 §2). Relê a cada `INTERVALO_DAS_PREPARACOES_MS` enquanto a lista tem algum caso; quando
 * um caso sai dela (a preparação terminou ou a falha foi superada), `onSairam` avisa a página, que
 * relê a tabela (a situação do caso pode ter mudado). `gatilho` reinicia o ciclo quando a página
 * relê por conta própria (um cadastro concluiu: as preparações dele começam). A falha de rede
 * mantém a última leitura e tenta de novo.
 *
 * E200.F2.C (F-E200.F2-01): a lista vazia não encerra a leitura. Enquanto a lista está à vista, a
 * descoberta segue a cada `INTERVALO_DA_DESCOBERTA_MS` (a preparação que outra aba ou outra pessoa
 * começou aparece sozinha); com a aba escondida, espera, e relê quando ela volta. Um só ciclo por vez.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useRef, useState } from 'react';

import { lerPreparacoes, type PreparacaoEmAndamentoData } from '@/lib/preparacoes';

export const INTERVALO_DAS_PREPARACOES_MS = 5000;
export const INTERVALO_DA_DESCOBERTA_MS = 15000;

const NENHUMA: ReadonlyMap<string, PreparacaoEmAndamentoData> = new Map();

function escondida(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

export function usePreparacoesEmAndamento(
  client: AxiosInstance,
  onSairam: () => void,
  gatilho?: unknown,
): ReadonlyMap<string, PreparacaoEmAndamentoData> {
  const [preparacoes, setPreparacoes] = useState(NENHUMA);
  const avisar = useRef(onSairam);
  avisar.current = onSairam;

  useEffect(() => {
    let ativo = true;
    let lendo = false;
    let espera: ReturnType<typeof setTimeout> | undefined;
    let vistos = new Set<string>();
    const agendar = (ms: number): void => {
      espera = setTimeout(() => {
        espera = undefined;
        void ciclo();
      }, ms);
    };
    const ciclo = async (): Promise<void> => {
      // Escondida, não lê: a volta da aba (`visibilitychange`) retoma o ciclo.
      if (lendo || escondida()) return;
      lendo = true;
      let lidas: PreparacaoEmAndamentoData[] | null = null;
      try {
        lidas = await lerPreparacoes(client);
      } catch {
        lidas = null;
      } finally {
        lendo = false;
      }
      if (!ativo) return;
      if (lidas !== null) {
        const agora = new Set(lidas.map((p) => p.case_id));
        if ([...vistos].some((id) => !agora.has(id))) avisar.current();
        vistos = agora;
        const novas = lidas;
        // Vazia de novo: o mesmo mapa, sem refazer a tela a cada descoberta.
        setPreparacoes((antes) =>
          antes.size === 0 && novas.length === 0
            ? antes
            : new Map(novas.map((p) => [p.case_id, p])),
        );
      }
      const vazia = lidas !== null && lidas.length === 0;
      agendar(vazia ? INTERVALO_DA_DESCOBERTA_MS : INTERVALO_DAS_PREPARACOES_MS);
    };
    const aoVoltar = (): void => {
      if (!escondida() && espera === undefined) void ciclo();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    void ciclo();
    return () => {
      ativo = false;
      document.removeEventListener('visibilitychange', aoVoltar);
      if (espera !== undefined) clearTimeout(espera);
    };
  }, [client, gatilho]);

  return preparacoes;
}
